import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import stylisRTLPlugin from '@mui/stylis-plugin-rtl';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import 'dayjs/locale/ar';

import { buildTypography, getComponents, themes } from '../config/themes';
import { DI_TOKENS } from '../constants';
import { useInjection } from '../hooks';
import { LocalStorageStore } from '../stores';

interface StyleCache {
  rtl: ReturnType<typeof createCache>;
  ltr: ReturnType<typeof createCache>;
}

// Create RTL cache with RTL plugin only (Emotion already includes prefixer)
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [stylisRTLPlugin],
});

const cacheLtr = createCache({
  key: 'muiltr',
});

const styleCache: StyleCache = {
  rtl: cacheRtl,
  ltr: cacheLtr,
};

const MuiProviderComponent: FC<PropsWithChildren> = observer(({ children }) => {
  const localStorageStore = useInjection<LocalStorageStore>(DI_TOKENS.LocalStorageStore);

  const { i18n } = useTrans();

  const [mounted, setMounted] = useState(false);
  const themeMode = localStorageStore.theme;
  const direction = localStorageStore.direction;

  // Setting the language of the document in the initial load of the app, and also when every it changes
  useEffect(() => {
    setMounted(true);
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  const extendedTheme = useMemo(
    () =>
      createTheme({
        ...themes[themeMode],
        direction,
        typography: buildTypography('Readex Pro'),
        components: getComponents(),
      }),
    [themeMode, direction],
  );

  if (!mounted) {
    return null;
  }

  return (
    <CacheProvider value={styleCache[direction === 'rtl' ? 'rtl' : 'ltr']}>
      <ThemeProvider theme={extendedTheme}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={i18n.language === 'ar' ? 'ar' : 'en'}
        >
          <CssBaseline />
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </CacheProvider>
  );
});

export const MuiProvider = MuiProviderComponent;
